/**
 * /settings/account — Account section wrapper (#851).
 *
 * Header + pill sub-nav + `<Outlet />` rendering the active tab.
 * All Account tabs are zero-prop self-scoped components (every
 * signed-in user manages their own profile / password / tokens /
 * notifications / account-delete) so no Outlet context is threaded
 * — children just render their existing component as-is.
 *
 * Routing: the `<Route index>` in App.tsx redirects bare
 * `/settings/account` to the first tab (`profile`); deep links go
 * straight to the matching child route.
 */
import { Outlet } from 'react-router-dom'
import { SettingsSubNav, type SettingsTab } from '../SettingsSubNav'

const ACCOUNT_TABS: readonly SettingsTab[] = [
  { to: 'profile',        label: 'Profile' },
  { to: 'password',       label: 'Password & sessions' },
  { to: 'tokens',         label: 'Personal access tokens' },
  { to: 'notifications',  label: 'Notifications' },
  { to: 'danger',         label: 'Delete account', danger: true },
]

/** Card-index helper: which tabs are visible for the current user?
 * All Account tabs are gate-`always` — every signed-in user sees
 * every tab — so this is a pure constant today. Surface kept as a
 * function for parity with the Workspace / Organization wrappers
 * (which DO compute role-filtered visibility), and so a future
 * gate (e.g., "tokens disabled for SSO-only orgs") is a one-line
 * change here without affecting the index page. */
export function getVisibleAccountTabs(): readonly SettingsTab[] {
  return ACCOUNT_TABS
}

export function AccountWrapper() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Account</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Settings tied to your user, not the workspace.
        </p>
      </div>
      <SettingsSubNav tabs={ACCOUNT_TABS} ariaLabel="Account settings sub-navigation" />
      <div className="pt-2">
        <Outlet />
      </div>
    </div>
  )
}
